import React, { useState } from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';

const QuoteIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" opacity="0.15">
    <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893
    1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167
    3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179zm10
    0C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335
    1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226
    3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179z" />
  </svg>
);

QuoteIcon.propTypes = {
  className: PropTypes.string,
};

const VerifiedBadge = ({ color }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M8 1C6.61553 1 5.26216 1.41054 4.11101 2.17971C2.95987 2.94888 2.06266 4.04213 1.53285
    5.32122C1.00303 6.6003 0.86441 8.00776 1.13451 9.36563C1.4046 10.7235 2.07129 11.9708 3.05026
    12.9497C4.02922 13.9287 5.2765 14.5954 6.63437 14.8655C7.99224 15.1356
    9.3997 14.997 10.6788 14.4672C11.9579 13.9373 13.0511 13.0401 13.8203
    11.889C14.5895 10.7378 15 9.38447 15 8C14.998 6.14409 14.2599 4.36477
    12.9476 3.05245C11.6352 1.74012 9.85591 1.00199 8 1ZM10.8535 6.6035L7.3535
    10.1035C7.25974 10.1972 7.13259 10.2499 7 10.2499C6.86742 10.2499 6.74027
    10.1972 6.6465 10.1035L5.1465 8.6035C5.05542 8.5092 5.00503 8.3829 5.00617
    8.2518C5.00731 8.1207 5.05989 7.99529 5.15259 7.90259C5.2453 7.80989 5.3707
    7.7573 5.5018 7.75616C5.6329 7.75502 5.7592 7.80542 5.8535 7.8965L7
    9.043L10.1465 5.8965C10.2408 5.80542 10.3671 5.75502 10.4982 5.75616C10.6293
    5.7573 10.7547 5.80989 10.8474 5.90259C10.9401 5.99529 10.9927 6.1207 10.9938
    6.2518C10.995 6.3829 10.9446 6.5092 10.8535 6.6035Z" fill={color} />
  </svg>
);

VerifiedBadge.propTypes = {
  color: PropTypes.string.isRequired,
};

const ReviewCard = ({ item, isFeatured }) => {
  if (isFeatured) {
    return (
      <div className="bg-[#1C2938] text-white rounded-2xl p-6 flex flex-col justify-between h-full">
        <div>
          <div className="flex items-start justify-between mb-3">
            {item.header && (
              <h3 className="font-bold text-xl flex-1 pr-4">
                {parse(item.header)}
              </h3>
            )}
            <QuoteIcon className="w-8 h-8 shrink-0 text-white" />
          </div>

          {item.body && (
            <div className="text-sm text-gray-300 mb-4">
              {parse(item.body)}
            </div>
          )}
        </div>

        <div className="flex justify-start lg:justify-end mr-8">
          <div>
            {item.image?.url && (
              <div className="mb-2">
                <img src={item.image.url} alt="Rating" className="h-5" />
              </div>
            )}
            {item.authorName && (
              <p className="font-bold text-sm">{item.authorName}</p>
            )}
            <div className="flex items-center gap-1 text-xs text-[#48C44F]">
              <VerifiedBadge color="#48C44F" />
              <span>Verified</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white text-[#1C2938] rounded-2xl p-6 flex flex-col justify-between">
      <div>
        <div className="flex items-center justify-between mb-3">
          {item.subtitle && (
            <span className="text-xs text-gray-500">
              {item.subtitle}
            </span>
          )}
          <QuoteIcon className="w-8 h-8 ml-auto text-[#1C2938]" />
        </div>

        {item.image?.url && (
          <div className="mb-3">
            <img src={item.image.url} alt="Rating" className="h-5" />
          </div>
        )}

        {item.header && (
          <h3 className="font-bold mb-2 text-sm">
            {parse(item.header)}
          </h3>
        )}

        {item.body && (
          <div className="text-sm text-gray-600 mb-4">
            {parse(item.body)}
          </div>
        )}
      </div>

      <div>
        {item.authorName && (
          <p className="font-bold text-sm">{item.authorName}</p>
        )}
        <div className="flex items-center gap-1 text-xs text-[#BFBFBF]">
          <VerifiedBadge color="#BFBFBF" />
          <span>Verified</span>
        </div>
      </div>
    </div>
  );
};

ReviewCard.propTypes = {
  item: PropTypes.shape({
    header: PropTypes.string,
    body: PropTypes.string,
    image: PropTypes.shape({
      url: PropTypes.string,
    }),
    authorName: PropTypes.string,
    subtitle: PropTypes.string,
    featured: PropTypes.bool,
  }).isRequired,
  isFeatured: PropTypes.bool.isRequired,
};

const MOBILE_LIMIT = 4;

const LandingPageReviewSection = ({ section }) => {
  const [showAll, setShowAll] = useState(false);
  const items = section.items || [];
  const featured = items.filter(item => item.featured);
  const standard = items.filter(item => !item.featured);

  // Distribute all standard items into 4 columns continuously.
  // First 4 go into cols 1-2, then featured tops cols 3-4,
  // next 4 go into cols 3-4 below featured, then remaining
  // round-robin across all 4 columns — no separate bottom section.
  const col1 = [];
  const col2 = [];
  const col3 = [];
  const col4 = [];
  const featuredItems = []; // { colIndex: 3, item }
  let stdIdx = 0;

  for (let f = 0; f < featured.length; f++) {
    // 4 standard items into cols 1-2
    if (stdIdx < standard.length) { col1.push(standard[stdIdx]); stdIdx++; }
    if (stdIdx < standard.length) { col2.push(standard[stdIdx]); stdIdx++; }
    if (stdIdx < standard.length) { col1.push(standard[stdIdx]); stdIdx++; }
    if (stdIdx < standard.length) { col2.push(standard[stdIdx]); stdIdx++; }

    // Featured card marker for cols 3-4
    featuredItems.push({ item: featured[f], position: col3.length });

    // 4 standard items into cols 3-4 below featured
    if (stdIdx < standard.length) { col3.push(standard[stdIdx]); stdIdx++; }
    if (stdIdx < standard.length) { col4.push(standard[stdIdx]); stdIdx++; }
    if (stdIdx < standard.length) { col3.push(standard[stdIdx]); stdIdx++; }
    if (stdIdx < standard.length) { col4.push(standard[stdIdx]); stdIdx++; }
  }

  // Remaining standard items round-robin across all 4 columns
  const allCols = [col1, col2, col3, col4];
  let colIdx = 0;
  while (stdIdx < standard.length) {
    allCols[colIdx % 4].push(standard[stdIdx]);
    stdIdx++;
    colIdx++;
  }

  return (
    <div
      className={`px-3 py-2 w-full ${section.customClasses || ''}`}
      style={{ backgroundColor: section.backgroundColor || '#F3F5F6' }}
    >
      {section.header && (
        <h2 className={section.headerClasses || 'text-3xl lg:text-4xl font-bold text-[#1C2938] text-center mb-10'}>
          {section.header}
        </h2>
      )}

      {/* Mobile: single column */}
      <div className="max-w-7xl mx-auto flex flex-col sm:hidden gap-2">
        {(showAll ? items : items.slice(0, MOBILE_LIMIT)).map((item, i) => (
          <ReviewCard key={i} item={item} isFeatured={!!item.featured} />
        ))}
        {!showAll && items.length > MOBILE_LIMIT && (
          <div className="flex justify-center mt-4">
            <button
              onClick={() => setShowAll(true)}
              className="w-full px-6 py-2 rounded-md border-2
                border-[#1C2938] bg-[#1C2938] text-[#ffffff] text-sm
                font-medium hover:opacity-100 transition-opacity"
            >
              More Reviews
            </button>
          </div>
        )}
      </div>

      {/* Desktop: 4-column masonry */}
      <div className="max-w-7xl mx-auto hidden sm:flex gap-4">
        {/* Column 1 */}
        <div className="flex-1 flex flex-col gap-4">
          {col1.map((item, i) => (
            <ReviewCard key={i} item={item} isFeatured={false} />
          ))}
        </div>

        {/* Column 2 */}
        <div className="flex-1 flex flex-col gap-4">
          {col2.map((item, i) => (
            <ReviewCard key={i} item={item} isFeatured={false} />
          ))}
        </div>

        {/* Columns 3-4: featured spans both, then individual cols below */}
        <div className="flex-[2] flex flex-col gap-4">
          {featuredItems.map((feat, i) => (
            <ReviewCard key={`feat-${i}`} item={feat.item} isFeatured={true} />
          ))}
          {(col3.length > 0 || col4.length > 0) && (
            <div className="flex gap-4">
              <div className="flex-1 flex flex-col gap-4">
                {col3.map((item, i) => (
                  <ReviewCard key={i} item={item} isFeatured={false} />
                ))}
              </div>
              <div className="flex-1 flex flex-col gap-4">
                {col4.map((item, i) => (
                  <ReviewCard key={i} item={item} isFeatured={false} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

LandingPageReviewSection.propTypes = {
  section: PropTypes.shape({
    typeOf: PropTypes.string.isRequired,
    header: PropTypes.string,
    headerClasses: PropTypes.string,
    customClasses: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({
      header: PropTypes.string,
      body: PropTypes.string,
      image: PropTypes.shape({
        url: PropTypes.string,
      }),
      authorName: PropTypes.string,
      subtitle: PropTypes.string,
      featured: PropTypes.bool,
    })),
    backgroundColor: PropTypes.string,
  }).isRequired,
};

export default LandingPageReviewSection;
